import { Box, IconButton, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { grey } from '@mui/material/colors';
import { ArrowBack, ArrowForward } from '@mui/icons-material';
import { useIsMobile } from '@uplift/hooks';
import { NavigationBackButton, ProviderLayout, ProviderLayoutProps, ScrollableView } from '@uplift/ui-v2';
import React, { useMemo, useState } from 'react';
import { DischargeNoteActionBarProps } from './DischargeNoteActionBar';
import { DischargeNoteSidebarContentProps } from './DischargeNoteSidebarContent';

export type DischargeNoteLayoutProps = {
  sidebarContent?: React.ReactElement<DischargeNoteSidebarContentProps>;
  actionBar?: React.ReactElement<DischargeNoteActionBarProps>;
  onBack?: () => void;
} & Pick<ProviderLayoutProps, 'background'>;

export const DischargeNoteLayout: React.FC<DischargeNoteLayoutProps> = ({
  background = 'white',
  ...props
}) => {
  const { classes } = useDischargeNoteLayoutStyles();
  const isMobile = useIsMobile();
  const [ sidebarOpen, setSidebarOpen ] = useState(false);

  const sidebar = useMemo(() => {
    if (!props.sidebarContent) {
      return null;
    }

    if (isMobile) {
      return (
        <Box width={sidebarOpen ? '330px' : 'unset'} height="100%" display="flex" flexDirection="column">
          <Box px={2} borderBottom={`1px solid ${grey[300]}`}>
            <div>
              <IconButton onClick={() => setSidebarOpen(p => !p)}>
                {sidebarOpen ? <ArrowForward color="secondary" /> : <ArrowBack color="secondary" />}
              </IconButton>
            </div>
          </Box>
          {sidebarOpen && (
            <ScrollableView flex={1}>
              {props.sidebarContent}
            </ScrollableView>
          )}
        </Box>
      );
    }

    return (
      <ScrollableView height="100%">
        {props.sidebarContent}
      </ScrollableView>
    );
  }, [ props.sidebarContent, isMobile, sidebarOpen ]);

  const navigationOverrideStart = useMemo(() => {
    if (props.onBack) {
      return (
        <Box height="100%" display="flex" alignItems="center">
          <NavigationBackButton onClick={() => props.onBack()}>
            Back To Edit
          </NavigationBackButton>
        </Box>
      );
    }
  }, [ props ]);

  return (
    <ProviderLayout
      logoLink=""
      background={background}
      navigationVariant="elevation"
      showClientsSearch={false}
      showNavigationLinks={false}
      bodyScroll={false}
      navigationOverrideStart={navigationOverrideStart}
    >
      <Box display="flex" height="100%">
        <ScrollableView flex={1}>
          <Box className={classes.content}>
            <Typography variant="h3" className={classes.title}>
              Discharge Note
            </Typography>
            {props.children}
          </Box>
        </ScrollableView>
        {sidebar && (
          <Box
            height="100%"
            bgcolor={grey[50]}
            borderLeft={`1px solid ${grey[300]}`}
            width={isMobile ? 'fit-content' : '25%'}
            minWidth={isMobile ? 'unset' : '402px'}
          >
            {sidebar}
          </Box>
        )}
      </Box>
      {props.actionBar}
    </ProviderLayout>
  );
};

const useDischargeNoteLayoutStyles = makeStyles({ name: 'DischargeNoteLayout' })(theme => ({
  content: {
    marginLeft: theme.spacing(3),
    marginRight: theme.spacing(3),
    marginTop: theme.spacing(8),
    paddingBottom: theme.spacing(15),
    maxWidth: '792px',
    [theme.breakpoints.up('lg')]: {
      marginLeft: theme.spacing(15),
    }
  },
  title: {
    marginBottom: theme.spacing(8),
  }
}));
